import { type JobEmploymentType, type JobPosting } from '@resolute/schema';
import { Container, Section } from '@resolute/ui';
import { useMemo, useState } from 'react';


import { useJobPostings } from './api';
import { ApplicationForm } from './components/application-form';
import { JobList } from './components/job-list';
import { CareersBenefits } from './sections/benefits';
import { CareersCulture } from './sections/culture';

import { Reveal } from '@/components/motion/reveal';
import { PageHero } from '@/components/page-hero';
import { clientEnv } from '@/lib/env';
import { Seo } from '@/lib/seo';

const APPLY_SECTION_ID = 'apply';

const pageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Careers',
  url: `${clientEnv.siteUrl}/careers`,
  isPartOf: {
    '@type': 'Organization',
    name: clientEnv.siteName,
    url: clientEnv.siteUrl,
  },
};

/** schema.org's JobPosting.employmentType enum. */
const GOOGLE_EMPLOYMENT_TYPE: Record<JobEmploymentType, string> = {
  'full-time': 'FULL_TIME',
  'part-time': 'PART_TIME',
  internship: 'INTERN',
  contract: 'CONTRACTOR',
};

/** A rolling 60-day validity — the model has no explicit expiry date, and
 *  Google requires *some* future `validThrough` for JobPosting rich results. */
const VALID_FOR_MS = 60 * 24 * 60 * 60 * 1000;

function jobPostingJsonLd(job: JobPosting) {
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description,
    datePosted: job.createdAt,
    validThrough: new Date(new Date(job.createdAt).getTime() + VALID_FOR_MS).toISOString(),
    employmentType: GOOGLE_EMPLOYMENT_TYPE[job.employmentType],
    hiringOrganization: {
      '@type': 'Organization',
      name: clientEnv.siteName,
      sameAs: clientEnv.siteUrl,
    },
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', addressLocality: job.location, addressCountry: 'IN' },
    },
  };
}

export default function CareersPage() {
  const { data: jobs, isLoading, isError } = useJobPostings();
  const [selectedJobId, setSelectedJobId] = useState<string>();

  const jsonLd = useMemo(() => [pageJsonLd, ...(jobs ?? []).map(jobPostingJsonLd)], [jobs]);

  const handleApply = (jobId: string) => {
    setSelectedJobId(jobId);
    document.getElementById(APPLY_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Seo
        title="Careers"
        description="Join Resolute Property Solutions — open roles across sales, marketing, legal, operations and technology, building a transparency-first real estate platform for India's growth corridors."
        path="/careers"
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="Careers"
        title="Help us build the platform"
        titleLine2="buyers actually trust."
        description="We're a fast-growing team solving real problems in real estate — verified inventory, honest numbers, and a first-class buyer journey. Come build it with us."
      />

      <CareersCulture />
      <CareersBenefits />

      <Section spacing="md">
        <Container>
          <Reveal className="max-w-2xl">
            <p className="text-sm font-medium uppercase tracking-widest text-secondary">
              Open positions
            </p>
            <h2 className="mt-2 text-balance font-display text-display-sm font-semibold tracking-tight">
              Current openings.
            </h2>
          </Reveal>

          <div className="mt-10 sm:mt-12">
            <JobList jobs={jobs} isLoading={isLoading} isError={isError} onApply={handleApply} />
          </div>
        </Container>
      </Section>

      <Section id={APPLY_SECTION_ID} spacing="md" className="scroll-mt-24 border-t border-border">
        <Container>
          <div className="mx-auto max-w-2xl">
            <Reveal>
              <p className="text-sm font-medium uppercase tracking-widest text-secondary">
                Apply
              </p>
              <h2 className="mt-2 text-balance font-display text-display-sm font-semibold tracking-tight">
                Tell us about yourself.
              </h2>
              <p className="mt-3 text-muted-foreground">
                Fields marked <span className="text-danger">*</span> are required. Don&apos;t see a
                role that fits? Submit a general application — we&apos;re always looking for great
                people.
              </p>
            </Reveal>

            <Reveal delay={0.08} className="mt-8 rounded-xl border border-border bg-card p-6 shadow-lg sm:p-7">
              <ApplicationForm jobs={jobs ?? []} selectedJobId={selectedJobId} />
            </Reveal>
          </div>
        </Container>
      </Section>
    </>
  );
}
